import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import IsNotActive from "../components/IsNotActive/IsNotActive";
import { indexStore } from "../store/indexStore";
import { Header } from "../components/header";
import Visibility from "../components/utils/Visibility";
import { Skeleton } from "../components/ui/skeleton";
import Iterator from "../components/utils/Iterator";
import { IoPhonePortraitOutline } from "react-icons/io5";
import { MdOutlinePhoneInTalk } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { CiLocationOn } from "react-icons/ci";
import BranchModel from "../Models/BranchModel";
import RestaurantModel from "../Models/RestaurantModel";

function Restaurant() {
  const { domain } = useParams();
  const {
    name,
    fetchBranchesAction,
    branchesLoading,
    branches,
    description,
    restaurant,
  } = indexStore();

  useEffect(() => {
    if (domain) {
      fetchBranchesAction({ domain: domain });
    }
  }, [domain, fetchBranchesAction]);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const onClickBranch = (id: number) => {
    navigate(`${id}`);
  };

  const nameKey = name as keyof RestaurantModel;

  return (
    <IsNotActive>
      <title>{domain?.toUpperCase()}</title>
      <Header backgroundColorfull noBack={true} />
      <Visibility visible={!branchesLoading}>
        <section className="flex flex-col gap-6 flex-grow text-onBackground min-h-screen">
          {/* ✅ ترويسة ملونة بهوية المطعم */}
          <div className="bg-primary text-onPrimary p-6 md:p-8 rounded-b-2xl md:rounded-b-3xl shadow-lg flex flex-col gap-3 md:gap-4 items-center text-center opacity-0 animate-fadeIn">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
              {t("gloabl.welcome")}{" "}
              {typeof restaurant[nameKey] === "string"
                ? String(restaurant[nameKey] || "").toUpperCase()
                : restaurant[nameKey] ?? ""}
            </h1>
            <p className="text-base md:text-lg opacity-90">{t("gloabl.ourBranches")}</p>
          </div>

          {/* ✅ قائمة الفروع */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 p-4 md:p-6">
            <Iterator
              data={branches}
              render={(branch, index) => (
                <BranchCard
                  key={branch.id}
                  branch={branch}
                  onClick={onClickBranch}
                  name={name as keyof BranchModel}
                  description={description as keyof BranchModel}
                  index={index}
                />
              )}
            />
          </div>
        </section>
      </Visibility>
      <Visibility visible={branchesLoading}>
        <RenderLoading />
      </Visibility>
    </IsNotActive>
  );
}

export default Restaurant;

const RenderLoading = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 p-4 md:p-6 pb-20">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className="flex flex-col gap-4 p-5 border rounded-xl">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    ))}
  </div>
);

const BranchCard = ({
  branch,
  onClick,
  name,
  description,
  index,
}: {
  branch: BranchModel;
  onClick: (id: number) => void;
  name: keyof BranchModel;
  description: keyof BranchModel;
  index: number;
}) => {
  const addressKey = name === "name_ar" ? "address_ar" : "address_en";
  
  return (
    <div
      key={branch.id}
      className="group flex flex-col gap-3 md:gap-4 p-5 md:p-6 bg-card border-2 border-border rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 hover:border-primary/50 hover:-translate-y-1 opacity-0 translate-y-4 animate-fadeInUp"
      style={{
        animationDelay: `${index * 100}ms`,
      }}
      onClick={() => onClick(branch.id)}
    >
      {/* العنوان */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
          <CiLocationOn className="text-primary w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg md:text-xl font-bold text-primary mb-1 line-clamp-2">
            {typeof branch[name] === "string"
              ? String(branch[name]).toUpperCase()
              : branch[name]}
          </h3>
          {branch[description] && (
            <p className="text-sm md:text-base text-muted-foreground line-clamp-2">
              {branch[description]}
            </p>
          )}
        </div>
      </div>

      {/* العنوان الكامل */}
      {branch[addressKey] && (
        <div className="flex gap-2 items-start pt-2 border-t border-border/50">
          <CiLocationOn className="text-primary w-4 h-4 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-foreground flex-1">{branch[addressKey]}</p>
        </div>
      )}

      {/* معلومات الاتصال */}
      <div className="flex flex-col gap-2 pt-2">
        <Visibility visible={branch.phone !== ""}>
          <a
            href={`tel:${branch.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="flex gap-2 items-center text-sm text-foreground hover:text-primary transition-colors group/item"
          >
            <div className="p-1.5 rounded-md bg-primary/10 group-hover/item:bg-primary/20 transition-colors">
              <MdOutlinePhoneInTalk className="text-primary w-4 h-4" />
            </div>
            <span dir="ltr" className="flex-1">{branch.phone}</span>
          </a>
        </Visibility>

        <Visibility visible={branch.mobile !== ""}>
          <a
            href={`tel:${branch.mobile}`}
            onClick={(e) => e.stopPropagation()}
            className="flex gap-2 items-center text-sm text-foreground hover:text-primary transition-colors group/item"
          >
            <div className="p-1.5 rounded-md bg-primary/10 group-hover/item:bg-primary/20 transition-colors">
              <IoPhonePortraitOutline className="text-primary w-4 h-4" />
            </div>
            <span dir="ltr" className="flex-1">{branch.mobile}</span>
          </a>
        </Visibility>
      </div>
    </div>
  );
};
